'use client';

import { useEffect, useState } from 'react';

const API_BASE_URL = process.env.NEXT_PUBLIC_API_BASE_URL || '';

/**
 * The marketing site's contact addresses, served from SuperAdmin →
 * Settings (Business Profile) via GET /api/system-settings/contacts.
 *
 * The fallbacks are the historical hardcoded inboxes, so the page renders
 * a working mailto immediately (and still works offline / on an
 * unconfigured install); the fetched values replace them when they arrive.
 * Module-level cache: many components on one page share one request.
 */
// Platform-wide (SaaS) fallbacks — owner decision 2026-09-29: EVERY email
// falls back to support@kayease.com and the phone to 9887664666. These are
// for KAYMPUS surfaces only; a school's pages fall back to that school's
// own contact details, never to these.
export const FALLBACK_CONTACTS = {
    salesEmail: 'support@kayease.com',
    supportEmail: 'support@kayease.com',
    supportPhone: '9887664666',
    privacyEmail: 'support@kayease.com',
    legalEmail: 'support@kayease.com',
};

/** '9887664666' → '+919887664666' for tel: links. */
export const phoneTel = (p) => {
    const d = String(p || '').replace(/\D/g, '');
    if (!d) return '';
    if (d.length === 10) return `+91${d}`;
    return `+${d}`;
};

/** '9887664666' → '+91 98876 64666' for display. */
export const phoneDisplay = (p) => {
    const d = String(p || '').replace(/\D/g, '');
    const ten = d.length === 12 && d.startsWith('91') ? d.slice(2) : d;
    if (ten.length !== 10) return p || '';
    return `+91 ${ten.slice(0, 5)} ${ten.slice(5)}`;
};

let cached = null;
let inFlight = null;

const fetchContacts = () => {
    if (cached) return Promise.resolve(cached);
    if (!inFlight) {
        inFlight = fetch(`${API_BASE_URL}/api/system-settings/contacts`)
            .then((r) => r.json())
            .then((data) => {
                cached = { ...FALLBACK_CONTACTS, ...(data?.contacts || {}) };
                return cached;
            })
            .catch(() => FALLBACK_CONTACTS)
            .finally(() => { inFlight = null; });
    }
    return inFlight;
};

export default function usePublicContacts() {
    const [contacts, setContacts] = useState(cached || FALLBACK_CONTACTS);
    useEffect(() => {
        let alive = true;
        fetchContacts().then((c) => { if (alive) setContacts(c); });
        return () => { alive = false; };
    }, []);
    return contacts;
}
